import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Audience } from '../models/Audience'
import { audienceSchema } from '../models/schema'
import {
  createAudience as createAudiencesAPI,
  deleteAudience as deleteAudiencesAPI,
  fetchAudienceById,
  fetchAudiences,
  updateAudience as updateAudiencesAPI,
} from '../services/AudiencesService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Audience> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'audiences',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getAudiencesSuccess,
  setQuery: setAudienceQuery,
  reset: resetAudienceState,
  resetQueryAndIds: resetAudienceQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Audience }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectAudiences = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedAudience = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectAudienceById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectAudiencesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedAudience =
  (audiences: Audience | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Audience>(audiences)))

export const getAudiences = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchAudiences(params),
    [audienceSchema],
    slice.actions
  )

export const createAudience = (audiences: Audience) =>
  SBAPI.CreateDispatched(
    createAudiencesAPI(audiences),
    audienceSchema,
    slice.actions
  )

export const getAudienceWithId = (id: number) =>
  SBAPI.FetchDispatched<Audience>(
    fetchAudienceById(id),
    audienceSchema,
    slice.actions
  )

export const updateAudience = (audiences: Audience) =>
  SBAPI.UpdateDispatched<Audience>(
    updateAudiencesAPI(audiences),
    audiences,
    slice.actions
  )

export const deleteAudience = (audiences: Audience) =>
  SBAPI.DestroyDispatched<Audience>(
    deleteAudiencesAPI(audiences),
    audiences,
    slice.actions
  )
